import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, ShieldAlert, Clock, ExternalLink, Send, User } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

const STATUS_META = {
  verified: { icon: ShieldCheck, tone: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", label: "VERIFIED" },
  processing: { icon: Clock, tone: "text-blue-300 bg-blue-500/10 border-blue-500/30", label: "PROCESSING" },
  requires_input: { icon: ShieldAlert, tone: "text-orange-300 bg-orange-500/10 border-orange-500/30", label: "NEEDS INPUT" },
  canceled: { icon: ShieldAlert, tone: "text-white/50 bg-white/5 border-white/10", label: "CANCELED" },
  not_started: { icon: User, tone: "text-white/40 bg-white/5 border-white/10", label: "NOT STARTED" },
};

export default function AdminIdentity() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | verified | pending | not_started
  const [sending, setSending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/identity/customers");
      setCustomers(data);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = customers.filter((c) => {
    if (filter === "all") return true;
    const s = c.identity?.status || "not_started";
    if (filter === "verified") return s === "verified";
    if (filter === "pending") return s === "processing" || s === "requires_input";
    if (filter === "not_started") return s === "not_started" || !c.identity;
    return true;
  });

  const sendVerification = async (customer_id) => {
    setSending(customer_id);
    try {
      const { data } = await api.post("/admin/identity/sessions", { customer_id });
      // Copy the URL for easy sharing
      const url = data?.identity?.session_url;
      if (url) {
        await navigator.clipboard.writeText(url).catch(() => {});
        toast.success("Verification link created & copied to clipboard");
      } else {
        toast.success("Verification session created");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSending(null); }
  };

  const counts = {
    all: customers.length,
    verified: customers.filter((c) => c.identity?.status === "verified").length,
    pending: customers.filter((c) => ["processing", "requires_input"].includes(c.identity?.status)).length,
    not_started: customers.filter((c) => !c.identity || c.identity?.status === "not_started").length,
  };

  return (
    <div data-testid="admin-identity-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/admin" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37]"><ArrowLeft size={14} /> Back to Dashboard</Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        <div className="label-eyebrow mb-3">Compliance · Stripe Identity</div>
        <h1 className="font-display text-4xl md:text-5xl mb-6" style={{ fontWeight: 300 }}>
          ID & License <span className="italic text-[#D4AF37]">Verification</span>
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {["all", "verified", "pending", "not_started"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`admin-identity-filter-${f}`}
              className={`text-left border rounded-lg p-4 transition ${
                filter === f ? "border-[#D4AF37]/60 bg-[#D4AF37]/5" : "border-white/10 bg-[#0a0a0a] hover:border-white/20"
              }`}
            >
              <div className="text-[10px] tracking-widest text-white/50 mb-1">{f.replace("_", " ").toUpperCase()}</div>
              <div className="font-display text-2xl text-white" style={{ fontWeight: 300 }}>{counts[f]}</div>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-white/10 border-dashed rounded-lg p-10 text-center text-white/40 text-sm">
            No customers match this filter.
          </div>
        ) : (
          <div className="border border-white/10 rounded-lg overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="bg-white/5 text-[10px] tracking-widest text-white/50">
                  <th className="text-left px-4 py-3">CUSTOMER</th>
                  <th className="text-left px-4 py-3">STATUS</th>
                  <th className="text-left px-4 py-3">VERIFIED NAME</th>
                  <th className="text-left px-4 py-3">DOB</th>
                  <th className="text-left px-4 py-3">LICENCE #</th>
                  <th className="text-left px-4 py-3">EXPIRY</th>
                  <th className="text-right px-4 py-3">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const status = c.identity?.status || "not_started";
                  const Meta = STATUS_META[status] || STATUS_META.not_started;
                  const Icon = Meta.icon;
                  const v = c.identity?.verified_outputs || {};
                  return (
                    <tr key={c.id} className="border-t border-white/10">
                      <td className="px-4 py-3">
                        <div className="text-white">{c.full_name}</div>
                        <div className="text-xs text-white/40">{c.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] tracking-widest ${Meta.tone}`}>
                          <Icon size={11} /> {Meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/70">{v.first_name || v.last_name ? `${v.first_name || ""} ${v.last_name || ""}`.trim() : "—"}</td>
                      <td className="px-4 py-3 text-white/70">{v.dob || "—"}</td>
                      <td className="px-4 py-3 text-white/70 font-mono text-xs">{v.id_number || "—"}</td>
                      <td className="px-4 py-3 text-white/70">{v.expiration_date || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-3">
                          {status !== "verified" && (
                            <button
                              onClick={() => sendVerification(c.id)}
                              disabled={sending === c.id}
                              data-testid={`admin-identity-send-${c.id}`}
                              className="text-xs text-[#D4AF37] hover:text-[#F6ECCC] tracking-widest inline-flex items-center gap-1 disabled:opacity-50"
                            >
                              <Send size={11} /> {sending === c.id ? "…" : status === "not_started" ? "REQUEST" : "RESEND"}
                            </button>
                          )}
                          {c.identity?.session_url && status !== "verified" && (
                            <a
                              href={c.identity.session_url}
                              target="_blank" rel="noopener noreferrer"
                              data-testid={`admin-identity-open-${c.id}`}
                              className="text-xs text-white/60 hover:text-[#D4AF37] tracking-widest inline-flex items-center gap-1"
                            >LINK <ExternalLink size={11} /></a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 text-xs text-white/40 border border-white/10 rounded p-4 leading-relaxed">
          Powered by <span className="text-white">Stripe Identity</span> — verified driver's licence data
          (name, DOB, licence number, expiry) flows back automatically via webhook. Customers can also
          self-verify from their Portal &gt; Profile tab.
        </div>
      </main>
    </div>
  );
}
